import { useCallback, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import type { useDisclosureWithContext } from '../../../hooks';
import { VStack } from '../../Core';
import { Dialog } from '../../Dialog';
import { FormGroup, Input } from '../../Form';
import { Text } from '../../Text';

export interface EntityAdminConfirmationDialogContext {
  ConfirmReason: string;
  ConfirmDetails?: ReactNode;
  RequiresConfirmText: boolean;
  ConfirmAction: () => Promise<any>;
}

export const EntityAdminConfirmationDialog = ({
  context,
  ...dialog
}: Pick<
  ReturnType<typeof useDisclosureWithContext<EntityAdminConfirmationDialogContext>>,
  'context' | 'isOpen' | 'close'
>) => {
  const [confirmText, setConfirmText] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (isConfirming || context?.ConfirmAction == null) {
      return;
    }

    flushSync(() => setIsConfirming(true));
    try {
      await context.ConfirmAction();
      setConfirmText('');
    } finally {
      setIsConfirming(false);
    }
  }, [context, isConfirming]);

  return (
    <Dialog
      {...dialog}
      confirmDisabled={context?.RequiresConfirmText && confirmText !== 'confirm'}
      confirmLoading={isConfirming}
      preventClosing={isConfirming}
      dataTestId="entity-admin-confirmation-dialog"
      confirmLabel="Confirm"
      onConfirm={handleConfirm}
      onCancel={() => setConfirmText('')}
    >
      <VStack alignItems="flex-start" gap="spacing8">
        <Text color="colorTextImportant" mr="spacing8" whiteSpace="nowrap">
          {context?.ConfirmReason}
        </Text>
        {context?.ConfirmDetails}
        {context?.RequiresConfirmText && (
          <FormGroup label={`To continue, type "confirm" below:`} mb="0" w="100%">
            <Input
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              data-testid="dialog-confirm-input"
            />
          </FormGroup>
        )}
      </VStack>
    </Dialog>
  );
};
